<script setup>
import { computed } from 'vue';
import { useUnreadBadgeSettings } from 'dashboard/composables/useUnreadBadgeSettings';

const props = defineProps({
  count: {
    type: Number,
    default: 0,
  },
  size: {
    type: String,
    default: 'md', // sm, md, lg
  },
});

const {
  badgeColor,
  badgeHexColor,
  animationEnabled,
  getAnimationClasses,
  getDisplayText,
} = useUnreadBadgeSettings();

const displayText = computed(() => getDisplayText(props.count));
const animationClasses = computed(() => getAnimationClasses(props.count));

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'size-4 unread-badge-sm';
    case 'lg':
      return 'size-7 text-sm';
    default:
      return 'size-5 text-xs';
  }
});

const containerClasses = computed(() => {
  const baseClasses =
    'inline-flex items-center justify-center rounded-full flex-shrink-0';
  return `${baseClasses} ${sizeClasses.value} ${animationClasses.value}`;
});

const badgeStyle = computed(() => {
  const color = badgeHexColor.value;
  return {
    '--badge-bg-color': color,
    backgroundColor: color,
  };
});
</script>

<template>
  <div v-if="count > 0" :class="containerClasses" :style="badgeStyle">
    <span class="font-semibold unread-badge-text">
      {{ displayText }}
    </span>
  </div>
</template>

<style scoped>
/* Animation keyframes */
@keyframes bounce {
  0%,
  100% {
    transform: translateY(-5%);
    animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
  }
  50% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.animate-bounce {
  animation: bounce 1s infinite;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Custom size for small badges - 9px font */
.unread-badge-sm {
  font-size: 9px !important;
}

/* Badge text */
.unread-badge-text {
  color: white;
  text-shadow: none;
}

:global(.dark) .unread-badge-text {
  color: black !important;
  text-shadow: none;
}
</style>
